import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useReportApi, useReportDisplay } from '../hooks/useReportApi';
import '../styles/result.style.css';
import logoHeader from '../assets/images/logo-header.png';
import logo from '../assets/images/logo.png';

interface ReportData {
  success: boolean;
  message: string;
  reportId: string;
  reportData: any; // Will be StructuredReportData
  generatedAt: string;
}

const ResultTestPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [documentId, setDocumentId] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  
  const {
    generateAndGetReport,
    regenerateReport,
    isGenerating,
    isRegenerating,
    generationError,
    regenerationError
  } = useReportApi();



  // Get documentId from URL params or location state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const docId = urlParams.get('docId') || location.state?.documentId;
    
    if (docId) {
      setDocumentId(docId);
    }
  }, [location]);

  // Load report data from location state if available
  useEffect(() => {
    if (location.state?.reportData) {
      setReportData(location.state.reportData);
    }
  }, [location.state]);

  const handleGenerateReport = async () => {
    if (!documentId) {
      alert('Please provide a document ID');
      return;
    }

    try {
      const result = await generateAndGetReport(documentId, {
        name: 'Test User',
        email: 'test@example.com',
        company: 'Test Company'
      });
      
      if (result.report) {
        setReportData({
          success: true,
          message: 'Report generated successfully',
          reportId: result.report.data.reportId,
          reportData: result.report.data.reportContent,
          generatedAt: result.report.data.generatedAt
        });
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  };

  const handleRegenerateReport = async () => {
    if (!reportData?.reportId) {
      alert('No report to regenerate');
      return;
    }

    try {
      const result = await regenerateReport(reportData.reportId, customPrompt || undefined);
      
      if (result.success) {
        setReportData(prev => prev ? {
          ...prev,
          reportData: result.reportData,
          generatedAt: result.regeneratedAt
        } : null);
      }
    } catch (error) {
      console.error('Failed to regenerate report:', error);
    }
  };

  const renderReportContent = () => {
    if (!reportData?.reportData) return null;

    const data = reportData.reportData;

    return (
      <div className="report-content">
        {/* Header with Persona Info */}
        <div className="report-header">
          <h1>AI Readiness Report</h1>
          {data.persona && (
            <div className="persona-info">
              <h2>{data.persona.emoji} {data.persona.name}</h2>
              <p><strong>Mindset:</strong> {data.persona.mindset}</p>
              <p><strong>Capability Level:</strong> {data.persona.capabilityLevel}</p>
            </div>
          )}
        </div>

        {/* Summary */}
        {data.summary && (
          <div className="report-section">
            <h2>Persona Summary</h2>
            <p>{data.summary}</p>
          </div>
        )}

        {/* Collaboration Profile */}
        {data.collaboration && (
          <div className="report-section">
            <h2>Collaboration Profile</h2>
            <div className="collaboration-grid">
              <div className="collaboration-item">
                <strong>Easiest Collaboration:</strong> {data.collaboration.easiest}
              </div>
              <div className="collaboration-item">
                <strong>Hardest Collaboration:</strong> {data.collaboration.hardest}
              </div>
              <div className="collaboration-item">
                <strong>Teamwork Tip:</strong> {data.collaboration.teamworkTip}
              </div>
            </div>
          </div>
        )}

        {/* PRIMAL Pillars */}
        {data.primalPillars && data.primalPillars.length > 0 && (
          <div className="report-section">
            <h2>PRIMAL Pillars</h2>
            <div className="pillars-table">
              <table>
                <thead>
                  <tr>
                    <th>Pillar</th>
                    <th>Description</th>
                    <th>Score</th>
                    <th>Strength</th>
                    <th>Focus Area</th>
                  </tr>
                </thead>
                <tbody>
                  {data.primalPillars.map((pillar, index) => (
                    <tr key={index}>
                      <td>{pillar.pillar}</td>
                      <td>{pillar.description}</td>
                      <td>{pillar.score}/5</td>
                      <td>{pillar.strength}</td>
                      <td>{pillar.focusArea}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Strategic Value */}
        {data.strategicValue && (
          <div className="report-section">
            <h2>Strategic Value</h2>
            <p>{data.strategicValue}</p>
          </div>
        )}

        {/* Action Plan */}
        {data.actionPlan && (
          <div className="report-section">
            <h2>Action Plan</h2>
            <div className="action-plan">
              {data.actionPlan.now && data.actionPlan.now.length > 0 && (
                <div className="action-period">
                  <h3>Now (0-30 days)</h3>
                  <ul>
                    {data.actionPlan.now.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.actionPlan.next && data.actionPlan.next.length > 0 && (
                <div className="action-period">
                  <h3>Next (30-60 days)</h3>
                  <ul>
                    {data.actionPlan.next.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.actionPlan.long && data.actionPlan.long.length > 0 && (
                <div className="action-period">
                  <h3>Long (60-90+ days)</h3>
                  <ul>
                    {data.actionPlan.long.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tools */}
        {data.tools && data.tools.length > 0 && (
          <div className="report-section">
            <h2>AI in Action: Tools to Explore</h2>
            <ul>
              {data.tools.map((tool, index) => (
                <li key={index}>{tool}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Prompts */}
        {data.prompts && data.prompts.length > 0 && (
          <div className="report-section">
            <h2>Ideas in Action: Try These Prompts</h2>
            <ul>
              {data.prompts.map((prompt, index) => (
                <li key={index}>"{prompt}"</li>
              ))}
            </ul>
          </div>
        )}

        {/* Report Metadata */}
        <div className="report-metadata">
          <p><strong>Report ID:</strong> {reportData.reportId}</p>
          <p><strong>Generated At:</strong> {new Date(reportData.generatedAt).toLocaleString()}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="result-test-page">
      {/* Header */}
      <header className="result-header">
        <div className="header-content">
          <img src={logoHeader} alt="Logo" className="header-logo" />
          <h1>AI Readiness Report Test</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="result-main">
        {/* Controls Section */}
        <div className="controls-section">
          <div className="control-group">
            <label htmlFor="documentId">Document ID:</label>
            <input
              id="documentId"
              type="text"
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
              placeholder="Enter document ID"
              className="input-field"
            />
          </div>

         

          <div className="button-group">
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating || !documentId}
              className="btn btn-primary"
            >
              {isGenerating ? 'Generating Report...' : 'Generate Report'}
            </button>

            <button
              onClick={handleRegenerateReport}
              disabled={isRegenerating || !reportData?.reportId}
              className="btn btn-secondary"
            >
              {isRegenerating ? 'Regenerating...' : 'Regenerate Report'}
            </button>

            <button
              onClick={() => navigate('/')}
              className="btn btn-outline"
            >
              Back to Home
            </button>
          </div>
        </div>

        {/* Error Messages */}
        {generationError && (
          <div className="error-message">
            <h3>Generation Error:</h3>
            <p>{generationError}</p>
          </div>
        )}

        {regenerationError && (
          <div className="error-message">
            <h3>Regeneration Error:</h3>
            <p>{regenerationError}</p>
          </div>
        )}

        {/* Loading States */}
        {(isGenerating || isRegenerating) && (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>{isGenerating ? 'Generating your AI Readiness Report...' : 'Regenerating report...'}</p>
          </div>
        )}

        {/* Report Content */}
        {reportData && renderReportContent()}

        {/* Raw Data Display (for debugging) */}
        {reportData && (
          <details className="raw-data-section">
            <summary>Raw Report Data (Debug)</summary>
            <pre className="raw-data">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </details>
        )}
      </main>

      {/* Footer */}
      <footer className="result-footer">
        <div className="footer-content">
          <img src={logo} alt="Logo" className="footer-logo" />
          <p>&copy; 2024 AI Readiness Assessment. All rights reserved.</p>
        </div>
      </footer>

      {/* Styles */}
      <style>{`
        .result-test-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          font-family: 'Inter', sans-serif;
        }

        .result-header {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 1rem 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .header-logo {
          height: 40px;
        }

        .header-content h1 {
          color: white;
          margin: 0;
          font-size: 1.5rem;
        }

        .result-main {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .controls-section {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .control-group {
          margin-bottom: 1.5rem;
        }

        .control-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
        }

        .input-field, .textarea-field {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .input-field:focus, .textarea-field:focus {
          outline: none;
          border-color: #667eea;
        }

        .textarea-field {
          resize: vertical;
          min-height: 80px;
        }

        .button-group {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #667eea;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #5a67d8;
        }

        .btn-secondary {
          background: #48bb78;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #38a169;
        }

        .btn-outline {
          background: transparent;
          color: #667eea;
          border: 2px solid #667eea;
        }

        .btn-outline:hover:not(:disabled) {
          background: #667eea;
          color: white;
        }

        .error-message {
          background: #fed7d7;
          border: 1px solid #feb2b2;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .error-message h3 {
          color: #c53030;
          margin: 0 0 0.5rem 0;
        }

        .error-message p {
          color: #742a2a;
          margin: 0;
        }

        .loading-message {
          text-align: center;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .report-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .report-header {
          text-align: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e5e7eb;
        }

        .report-header h1 {
          color: #1f2937;
          margin: 0 0 1rem 0;
          font-size: 2rem;
        }

        .persona-info {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem;
          border-radius: 12px;
          margin-top: 1rem;
        }

        .persona-info h2 {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
        }

        .persona-info p {
          margin: 0.5rem 0;
          font-size: 1.1rem;
        }

        .report-section {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .report-section:last-child {
          border-bottom: none;
        }

        .report-section h2 {
          color: #1f2937;
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .report-section h3 {
          color: #374151;
          font-size: 1.2rem;
          margin-bottom: 0.75rem;
        }

        .report-section p {
          margin-bottom: 1rem;
          line-height: 1.6;
          color: #374151;
        }

        .report-section ul {
          margin-bottom: 1rem;
          padding-left: 2rem;
        }

        .report-section li {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }

        .collaboration-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .collaboration-item {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid #667eea;
        }

        .collaboration-item strong {
          color: #1f2937;
          display: block;
          margin-bottom: 0.5rem;
        }

        .pillars-table {
          overflow-x: auto;
          margin-top: 1rem;
        }

        .pillars-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .pillars-table th {
          background: #667eea;
          color: white;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
        }

        .pillars-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .pillars-table tr:last-child td {
          border-bottom: none;
        }

        .pillars-table tr:nth-child(even) {
          background: #f9fafb;
        }

        .action-plan {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .action-period {
          background: #f9fafb;
          padding: 1.5rem;
          border-radius: 8px;
          border-left: 4px solid #48bb78;
        }

        .action-period h3 {
          color: #1f2937;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .action-period ul {
          margin: 0;
          padding-left: 1.5rem;
        }

        .action-period li {
          margin-bottom: 0.5rem;
          color: #374151;
        }

        .report-metadata {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
          font-size: 0.9rem;
          color: #6b7280;
        }

        .report-metadata p {
          margin: 0.25rem 0;
        }

        .raw-data-section {
          background: white;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 2rem;
        }

        .raw-data-section summary {
          cursor: pointer;
          font-weight: 600;
          color: #374151;
          padding: 0.5rem;
        }

        .raw-data {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          overflow-x: auto;
          font-size: 0.8rem;
          margin-top: 1rem;
        }

        .result-footer {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 2rem;
          text-align: center;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .footer-logo {
          height: 30px;
          margin-bottom: 1rem;
        }

        .footer-content p {
          color: white;
          margin: 0;
        }

        @media (max-width: 768px) {
          .result-main {
            padding: 1rem;
          }

          .controls-section {
            padding: 1rem;
          }

          .button-group {
            flex-direction: column;
          }

          .btn {
            width: 100%;
          }

          .report-content {
            padding: 1rem;
          }

          .report-header h1 {
            font-size: 1.5rem;
          }

          .persona-info h2 {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ResultTestPage;
